import { FC, memo, useContext, useEffect, useRef, useState } from 'react';

// components
import { ContextMenu } from '../../../ContextMenu';
import { Icon } from '@sdb/planning-ui';
import AppointmentContent from './components/AppointmentContent/AppointmentContent';
import { Box } from '@mui/material';
import { SummaryCard } from '../../../SummaryCard';
import Tooltip from '@mui/material/Tooltip';
import LoadingIcon from '../../../../../shared/LoadingIcon/LoadingIcon';

// constants
import {
  IS_ORIGINAL_PLACEHOLDER_ID,
  IS_PREVIEW_ID
} from '../../../AppointmentForm/AppointmentForm.consts';

// hooks
import { useTranslation } from 'react-i18next';
import { useSelector } from 'hooks';

// styles
import { CustomIcon } from 'components/mrp/mrp.styles';
import {
  CalendarAppointmentContainer,
  CalendarAppointmentBorder,
  CustomIconContainer,
  OriginalPlaceholderAppointmentContainer,
  PreviewAppointmentContainer,
  StyledCalendarContainerBox,
  StyledEntityTextTypography,
  TimeSlotBreachIndicator
} from './CalendarAppointment.styles';

// types
import type { EventProps } from 'react-big-calendar';
import type {
  CalendarAppointmentEvent,
  CalendarAppointmentPreviewEvent
} from '../../../Calendar/Calendar.types';
import { WorkPlaceResponse } from 'generated/index';
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';

// utils
import dayjs from 'dayjs';
import { themeExtension } from 'config/theme.extension';
import {
  buildEntityConfigs,
  getEntityLabel,
  hasNonMissingSyncStatus,
  createAppointmentFormDataFromEventResponse,
  extractEntityDataFromPlanningEvent,
  getOpenCalendarAction,
  sameDay
} from './CalenderAppointment.utils';
import { CalendarInteractionContext } from 'components/mrp/components/CalendarContainer/components/CalendarInteractionProvider/CalendarInteractionProvider.context';
import { getPlatform } from 'components/shared/Typography/Kbd/Kbd.utils';

// stores
import { useCalendarStore } from 'components/mrp/stores/calendarStore/calendarStore';
import { useAppointmentStore } from 'components/mrp/stores/appointmentStore/appointmentStore';
import { useShallow } from 'zustand/shallow';
import { useCalendarNavigationStore } from 'components/mrp/components/CalendarContainer/components/store/calendarNavigationStore.store';
import { useCalendarInteractionStore } from 'components/mrp/components/CalendarContainer/components/store/calendarInteractionStore.store';
import { isEqual } from 'lodash';

// consts
import { MrpShortcuts } from 'components/mrp/mrp.consts';
import CalendarAppointmentSignalRow from './CalendarAppointmentSignalRow';

const { palette } = themeExtension.custom!.constants.MRP;

const CalendarAppointment: FC<EventProps<CalendarAppointmentEvent>> = memo(
  ({ event }) => {
    'use memo';
    const { t } = useTranslation('common', { keyPrefix: 'mrp' });

    const hasAddAppointmentAccess = useSelector(
      ({ grants }) => grants.hasAddAppointmentAccess
    );

    const eventRef = useRef<HTMLElement | null>(null);
    const [tooltipOpen, setTooltipOpen] = useState(false);

    const { planningEvent, title, id } = event;
    const isPreview = id === IS_PREVIEW_ID;

    const calendarEntityIds = useCalendarStore(
      useShallow(
        (state) => state.entityIdsPerCalendarId[event.calendarId] || []
      )
    );
    const calendarIdsPerEntityId = useCalendarStore(
      useShallow((state) => state.calendarIdsPerEntityId)
    );
    const openedCalendarsEntityIds = Object.keys(calendarIdsPerEntityId);

    const openCalendar = useCalendarStore(
      (state) => state.control.openCalendar
    );

    const { timeSlotMin, timeSlotMax } = useCalendarStore(
      useShallow((state) => ({
        timeSlotMin: state.timeSlotMin,
        timeSlotMax: state.timeSlotMax
      }))
    );

    const { setAppointmentData } = useAppointmentStore(
      useShallow((state) => ({
        setAppointmentData: state.control.setAppointmentData
      }))
    );

    const isBeforeTimeSlotStart =
      sameDay(dayjs(event.end).toDate()).isBefore(sameDay(timeSlotMin)) ||
      sameDay(dayjs(event.start).toDate()).isBefore(sameDay(timeSlotMin));
    const isAfterTimeSlotEnd =
      sameDay(dayjs(event.start).toDate()).isAfter(sameDay(timeSlotMax)) ||
      sameDay(dayjs(event.end).toDate()).isAfter(sameDay(timeSlotMax));
    const hasTimeSlotBreach = isBeforeTimeSlotStart || isAfterTimeSlotEnd;

    const {
      otherEmployees,
      isEmployee,
      otherResources,
      isResource = false,
      otherClients,
      isClient,
      otherWorkPlace
    } = extractEntityDataFromPlanningEvent(event, calendarEntityIds);

    const hasNote =
      !!planningEvent?.notePublic || !!planningEvent?.noteInternal;
    const isRecurring = !!planningEvent?.isRecurring;
    const appointmentType = planningEvent?.appointmentType;
    const hasOverlap = !!planningEvent?.warnings?.hasOverlap;

    const hasSyncStatus = hasNonMissingSyncStatus(
      planningEvent?.synchronizationStatusSummary
    );

    // An appointment with an externalId does not need to have a synchronization status UI.
    const isNotInSync =
      hasSyncStatus &&
      !planningEvent?.externalId &&
      !!planningEvent?.synchronizationStatusSummary?.hasErrors;
    // hasNoSyncStatus covers two cases:
    // 1. No summary at all (export not configured or data not yet loaded).
    // 2. Summary exists but success===false and no errors — a defensive fallback.
    //    The backend now generates synthetic missingCalculation entries (with hasErrors=true)
    //    when no sync records exist, so this branch should not be reached in practice.
    //    It is kept as a safety net in case the summary is present without statuses.
    const hasNoSyncStatus =
      !planningEvent?.externalId &&
      (!hasSyncStatus ||
        !planningEvent?.synchronizationStatusSummary ||
        (planningEvent.synchronizationStatusSummary.success === false &&
          !planningEvent.synchronizationStatusSummary.hasErrors));
    // Imported appointments (externalId set) that are only partially imported.
    // Intentionally excluded from hasConflict — this is a data-quality signal, not a scheduling conflict.
    const isPartiallyImported =
      !!planningEvent?.externalId &&
      planningEvent?.importSyncStatus?.status === 'PARTIALLY_COMPLETE';

    // The treatment frozen in the export no longer matches the one this occurrence would be
    // derived to belong to today. Intentionally excluded from hasConflict for the same reason as
    // isPartiallyImported — a data-quality signal, not a scheduling conflict. An imported
    // appointment is never exported from here, so it can never deviate.
    const hasTreatmentDeviation =
      !planningEvent?.externalId &&
      !!planningEvent?.synchronizationStatusSummary?.hasTreatmentDeviation;

    // If summaries are missing, we don't consider this a conflict, as it is not a scheduling issue.
    const hasConflict =
      hasSyncStatus && (hasOverlap || hasNoSyncStatus || isNotInSync);

    const { selectedEvent, setEvent } = useCalendarNavigationStore(
      useShallow((state) => ({
        selectedEvent: state.event,
        setEvent: state.control.setEvent
      }))
    );
    const setPosition = useCalendarNavigationStore(
      (state) => state.control.setPosition
    );
    const isPositionOnEvent = useCalendarNavigationStore((state) => {
      const position = state.position;
      if (!position) return false;
      if (position.calendarId !== event.calendarId) return false;
      return (
        !position.date.isBefore(dayjs(event.start)) &&
        position.date.isBefore(dayjs(event.end))
      );
    });

    const isNavigationSelected = selectedEvent?.id === planningEvent?.id;

    // An appointment linked to several entities is rendered once per opened
    // calendar, and every copy matches on id alone. Only the copy in the calendar
    // the user actually interacted with may act as the popover anchor — otherwise
    // each copy overwrites the shared ref and the last one (the right-most, often
    // scrolled out of view) wins. See issue #2409.
    const isAnchorCopy =
      isNavigationSelected && selectedEvent?.calendarId === event.calendarId;

    const {
      addAppointment,
      deleteAppointment,
      duplicateEvent,
      duplicateRecurringEvent,
      exportAppointmentSeries
    } = useContext(CalendarInteractionContext);

    const {
      sourceEvent,
      isCut,
      isProcessing,
      setEventRef,
      setSourceEvent,
      setShowEntityQuickConnect,
      setShowEntityQuickDisconnect,
      showEntityQuickDisconnect
    } = useCalendarInteractionStore(
      useShallow((state) => ({
        sourceEvent: state.sourceEvent,
        isCut: state.isCut,
        isProcessing: state.isProcessing,
        setEventRef: state.control.setEventRef,
        setSourceEvent: state.control.setSourceEvent,
        setShowEntityQuickConnect: state.control.setShowEntityQuickConnect,
        setShowEntityQuickDisconnect:
          state.control.setShowEntityQuickDisconnect,
        showEntityQuickDisconnect: state.showEntityQuickDisconnect
      }))
    );

    useEffect(() => {
      if (isAnchorCopy) {
        setEventRef(eventRef);
      }
    }, [isAnchorCopy, setEventRef]);

    // Toggle tooltip with Space when selected AND the cell position is on this event.
    useEffect(() => {
      if (!isNavigationSelected || !isPositionOnEvent) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.code === 'Space') {
          const target = e.target;
          if (
            target instanceof HTMLInputElement ||
            target instanceof HTMLTextAreaElement ||
            (target instanceof HTMLElement && target.isContentEditable)
          )
            return;
          e.preventDefault();
          setTooltipOpen((prev) => !prev);
        }
        if (e.key === 'Escape') {
          setTooltipOpen(false);
        }
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isNavigationSelected, isPositionOnEvent]);

    // Close tooltip when this appointment loses navigation selection.
    useEffect(() => {
      if (!isNavigationSelected) setTooltipOpen(false);
    }, [isNavigationSelected]);

    const interactionData = {
      isInteracted: sourceEvent?.id == event.id,
      isCut,
      disableInteraction: !!showEntityQuickDisconnect
    };

    const isBeingProcessed =
      isProcessing && sourceEvent?.id == planningEvent?.id;

    const handleEditAppointment = () => {
      if (!planningEvent) return;

      const appointmentData =
        createAppointmentFormDataFromEventResponse(planningEvent);
      if (!appointmentData) return;

      setAppointmentData(appointmentData);
    };

    const handleDuplicateAppointment = () => {
      if (planningEvent?.isEditable) return;
      duplicateEvent(event);
    };

    const handleDuplicateRecurringAppointment = () => {
      if (planningEvent?.isEditable) return;
      duplicateRecurringEvent(event);
    };

    const handleExportAppointmentSeries = () => {
      exportAppointmentSeries(event);
    };

    const handleAddAppointment = () => {
      addAppointment(event.calendarId, dayjs(event.start));
    };

    const entityConfigs = buildEntityConfigs({
      palette,
      otherEmployees,
      otherClients,
      otherResources,
      isEmployee,
      isClient,
      isResource
    });

    const renderEntityInfo = (
      count: number,
      singleLabel: string | undefined,
      iconType: IconType,
      pluralLabel: string | undefined,
      isCurrentEntity?: boolean,
      iconColor?: string,
      isOverflowing: boolean = false
    ) => {
      const label = getEntityLabel({
        count,
        singleLabel,
        pluralLabel,
        isCurrentEntity
      });

      if (!label) return null;
      if (isOverflowing) return label;

      return (
        <CustomIconContainer iconColor={iconColor}>
          <Icon type={iconType} />
          <StyledEntityTextTypography variant="body1">
            {label}
          </StyledEntityTextTypography>
        </CustomIconContainer>
      );
    };

    const generateEntities = (isOverflowing = false) =>
      entityConfigs.map((entity) =>
        renderEntityInfo(
          entity.count,
          entity.singleLabel,
          entity.iconType,
          entity.pluralLabel,
          entity.isCurrentEntity,
          entity.iconColor,
          isOverflowing
        )
      );

    const [
      employeesOnAppointment,
      clientsOnAppointment,
      resourcesOnAppointment
    ] = generateEntities();

    const [
      employeesOnAppointmentOverflow,
      clientsOnAppointmentOverflow,
      resourcesOnAppointmentOverflow
    ] = generateEntities(true);

    const openCalendarAction = getOpenCalendarAction(
      openCalendar,
      openedCalendarsEntityIds,
      otherEmployees,
      otherClients,
      otherResources,
      otherWorkPlace as WorkPlaceResponse | undefined
    );

    const actions = [
      ...(!!openCalendarAction ? [openCalendarAction] : []),
      {
        value: t('copyAppointment'),
        onClick: () => setSourceEvent(event, false),
        icon: {
          iconType: 'copy' as IconType
        },
        shortcutHint: MrpShortcuts.COPY_APPOINTMENT,
        disabled: !hasAddAppointmentAccess
      },
      {
        value: t('cutAppointment'),
        onClick: () => setSourceEvent(event, true),
        icon: {
          iconType: 'scissors' as IconType
        },
        shortcutHint: MrpShortcuts.CUT_APPOINTMENT,
        disabled: !hasAddAppointmentAccess || !planningEvent?.isEditable
      },
      ...(!planningEvent?.isEditable
        ? [
            {
              value: t('duplicateAppointment'),
              onClick: handleDuplicateAppointment,
              icon: {
                iconType: 'clone' as IconType
              },
              shortcutHint: MrpShortcuts.DUPLICATE_APPOINTMENT,
              disabled: !hasAddAppointmentAccess
            },
            {
              value: t('duplicateRecurringAppointment'),
              onClick: handleDuplicateRecurringAppointment,
              icon: {
                iconType: 'clone' as IconType
              },
              shortcutHint: MrpShortcuts.DUPLICATE_RECURRING_APPOINTMENT,
              disabled: !hasAddAppointmentAccess
            }
          ]
        : []),
      {
        value: t('quickConnect'),
        onClick: () => {
          setEvent(event);
          setShowEntityQuickConnect(true);
        },
        icon: {
          iconType: 'user-plus' as IconType
        },
        shortcutHint: MrpShortcuts.QUICK_CONNECT,
        disabled: !hasAddAppointmentAccess || !planningEvent?.isEditable
      },
      {
        value: t('quickDisconnect'),
        onClick: () => {
          setSourceEvent(event, false, false);
          setShowEntityQuickDisconnect(true);
        },
        icon: {
          iconType: 'link-slash' as IconType
        },
        shortcutHint: MrpShortcuts.QUICK_DISCONNECT,
        disabled: !hasAddAppointmentAccess || !planningEvent?.isEditable
      },
      {
        value: t('editAppointment'),
        onClick: handleEditAppointment,
        icon: {
          iconType: 'pen-to-square' as IconType
        },
        shortcutHint: MrpShortcuts.EDIT_APPOINTMENT,
        disabled: !hasAddAppointmentAccess || !planningEvent?.isEditable
      },
      {
        value: t('deleteAppointment'),
        onClick: deleteAppointment.bind(
          null,
          createAppointmentFormDataFromEventResponse(planningEvent)
        ),
        icon: {
          iconType: 'trash-can' as IconType
        },
        shortcutHint:
          getPlatform() === 'mac'
            ? MrpShortcuts.DELETE_APPOINTMENT_MAC_OS
            : MrpShortcuts.DELETE_APPOINTMENT,
        disabled: !hasAddAppointmentAccess || !planningEvent?.isEditable
      },
      {
        value: t('exportAppointmentSeries'),
        onClick: handleExportAppointmentSeries,
        icon: {
          iconType: 'arrow-right-to-line' as IconType
        },
        disabled: !planningEvent?.isEditable
      },
      {
        value: t('toggleTooltip'),
        onClick: () => setTooltipOpen((prev) => !prev),
        icon: {
          iconType: 'circle-info' as IconType
        },
        shortcutHint: MrpShortcuts.TOGGLE_TOOLTIP
      },
      {
        value: t('addAppointment'),
        onClick: handleAddAppointment,
        icon: {
          iconType: 'calendar-plus' as IconType
        },
        shortcutHint: MrpShortcuts.NEW_APPOINTMENT,
        disabled: !hasAddAppointmentAccess,
        separated: true
      }
    ];

    // TODO: 1275 Remove after decision is made that we don't use colored borders in AppointmentPlanning.
    // if (event.resourceId == DAY_AVAILABILITIES) {
    //   return (
    //     <AvailabilityEvent
    //       event={event as unknown as CalendarAvailabilityEvent}
    //     />
    //   );
    // }

    if (isPreview) {
      const hasOverlap = (event as CalendarAppointmentPreviewEvent).hasOverlap;
      return (
        <PreviewAppointmentContainer hasOverlap={hasOverlap}>
          <CustomIcon
            type={
              hasOverlap
                ? ('triangle-exclamation' as const)
                : ('check' as const)
            }
          />
        </PreviewAppointmentContainer>
      );
    }

    if (id === IS_ORIGINAL_PLACEHOLDER_ID) {
      return <OriginalPlaceholderAppointmentContainer />;
    }

    return (
      <StyledCalendarContainerBox ref={eventRef}>
        <ContextMenu
          actions={actions}
          clickEvent="onContextMenu"
          id={event.id}
          onMenuOpen={() => {
            setEvent(event);
            setPosition({
              calendarId: event.calendarId,
              date: dayjs(event.start)
            });
          }}
        >
          <Tooltip
            arrow
            enterDelay={500}
            enterNextDelay={500}
            open={tooltipOpen}
            onOpen={() => setTooltipOpen(true)}
            onClose={() => setTooltipOpen(false)}
            title={<SummaryCard event={event} />}
          >
            <CalendarAppointmentBorder
              isNavigationSelected={isNavigationSelected}
              interactionData={interactionData}
            >
              <CalendarAppointmentContainer
                hasConflict={hasConflict}
                isRecurring={isRecurring}
                hasTimeSlotBreach={hasTimeSlotBreach}
                isReadOnly={!event.planningEvent.isEditable}
                interactionData={interactionData}
                className="calendarAppointmentContainer"
                sx={{
                  // TS does not recognize this property, but it is used for scroll state management in css
                  // MUI styled component does not recognize it either, so we can not add this in the styled component
                  // @ts-ignore-next-line
                  containerType: 'scroll-state',
                  containerName: 'card',
                  pl: 1.5,
                  pr: 4
                }}
              >
                <Box className="mainContentContainer">
                  {hasTimeSlotBreach && (
                    <TimeSlotBreachIndicator>
                      <Icon
                        type={
                          isBeforeTimeSlotStart
                            ? ('chevron-up' as const)
                            : ('chevron-down' as const)
                        }
                      />
                    </TimeSlotBreachIndicator>
                  )}
                  {isBeingProcessed ? (
                    <LoadingIcon size="SMALL" />
                  ) : (
                    <>
                      <Box className="normalLayout">
                        <AppointmentContent
                          title={title}
                          employeesOnAppointment={employeesOnAppointment}
                          clientsOnAppointment={clientsOnAppointment}
                          resourcesOnAppointment={resourcesOnAppointment}
                          workPlace={otherWorkPlace?.name}
                        />
                      </Box>

                      <Box className="explodedLayout">
                        <AppointmentContent
                          title={title}
                          employeesOnAppointment={
                            employeesOnAppointmentOverflow
                          }
                          clientsOnAppointment={clientsOnAppointmentOverflow}
                          resourcesOnAppointment={
                            resourcesOnAppointmentOverflow
                          }
                          workPlace={otherWorkPlace?.name}
                          isOverflowing
                        />
                      </Box>
                      <Box className="iconsLayout">
                        <CalendarAppointmentSignalRow
                          hasNote={hasNote}
                          appointmentType={appointmentType}
                          hasOverlap={hasOverlap}
                          isNotInSync={isNotInSync}
                          hasNoSyncStatus={hasNoSyncStatus}
                          isPartiallyImported={isPartiallyImported}
                          hasTreatmentDeviation={hasTreatmentDeviation}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              </CalendarAppointmentContainer>
            </CalendarAppointmentBorder>
          </Tooltip>
        </ContextMenu>
      </StyledCalendarContainerBox>
    );
  },
  (prevProps, nextProps) => {
    // Prevent re-rendering if the event remains unchanged
    return isEqual(prevProps, nextProps);
  }
);

export { CalendarAppointment };
